import { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { HelpCircle, X } from 'lucide-react';
import api from '../utils/api';

// Tiny help button that re-shows the welcome announcement to the current user
// on demand. Doesn't affect read state.
export default function WelcomeHelpButton({ size = 16, label = false }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const show = async () => {
    setOpen(true);
    if (data) return;
    setLoading(true);
    try {
      const r = await api.get('/announcements/welcome');
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={show}
        title={t('welcomeHelp.title')}
        aria-label={t('welcomeHelp.title')}
        className="btn btn-ghost btn-sm"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px' }}
      >
        <HelpCircle size={size} />
        {label && <span style={{ fontSize: 12 }}>{t('welcomeHelp.howToPlay')}</span>}
      </button>

      {open && ReactDOM.createPortal((
        <div
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => { if (e.target === e.currentTarget) { (() => setOpen(false))(e); } }}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 560, width: '100%',
              maxHeight: '85vh', overflowY: 'auto',
              margin: 0,
              position: 'relative',
              background:
                'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, transparent 60%), var(--c-surface)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.35), 0 0 0 1px rgba(251, 191, 36, 0.4)',
              animation: 'announcement-pop 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('common.close')}
              style={{
                position: 'absolute', top: 8, right: 8,
                background: 'transparent', border: 'none',
                cursor: 'pointer', padding: 4,
                color: 'var(--c-text-muted)',
              }}
            >
              <X size={18} />
            </button>

            {loading && <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>{t('common.loading')}…</div>}
            {!loading && data && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <span style={{ fontSize: 36, lineHeight: 1 }}>{data.emoji || '🎫'}</span>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
                    {i18n.language === 'en' ? data.title_en : data.title_es}
                  </h2>
                </div>
                <div style={{
                  fontSize: 14, lineHeight: 1.55, color: 'var(--c-text)',
                  whiteSpace: 'pre-wrap',
                }}>
                  {i18n.language === 'en' ? data.body_en : data.body_es}
                </div>
              </>
            )}
            {!loading && !data && (
              <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>
                {t('welcomeHelp.notFound')}
              </div>
            )}
          </div>
        </div>
      ), document.body)}
    </>
  );
}
